import React, { useContext, useState } from 'react';
import invoicingService from '../../services/api/invoicingService';
import { AuthContext } from '../../contexts/AuthContext';
const definiteRejections = new Set(['INVOICE_PROVIDER_CHANGED', 'INVOICE_RECONCILIATION_MISMATCH',
  'INVOICE_CLAIM_CHANGED', 'INVOICE_REVIEW_REQUIRED', 'INVOICE_ACCOUNT_CHANGED', 'INVOICE_NOT_UNCERTAIN', 'INVOICE_NOT_LEGACY']);

export default function LegacyInvoiceReconciliation(props) {
  const auth = useContext(AuthContext);
  if (!auth?.currentUser?.id || auth.currentUser.userType !== 'admin' || auth.isImpersonating) return <p>A current administrator session is required to reconcile this invoice.</p>;
  // A staff switch must not carry the prior actor's reason or pending command.
  return <ReconciliationForActor key={`${auth.currentUser.id}:${props.operation.id}`} {...props} />;
}

function ReconciliationForActor({ operation, onComplete }) {
  const [draft, setDraft] = useState('');
  const [review, setReview] = useState(null);
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const preview = async () => {
    setBusy(true); setError(''); setReview(null); setChecked(false);
    try {
      const value = await invoicingService.previewLegacyReconciliation(operation.id, Number(draft));
      if (value.preparationId !== operation.id || value.observation?.draftNumber !== Number(draft) || !/^[a-f0-9]{64}$/.test(value.observationHash || '')) throw new Error('The review response is incomplete. Work remains reserved.');
      setReview(value);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const confirm = async () => {
    const command = pending || { preparationId: operation.id, draftNumber: review.observation.draftNumber,
      observationHash: review.observationHash, reason: reason.trim() };
    setPending(command); setBusy(true); setError('');
    try {
      const receipt = await invoicingService.confirmLegacyReconciliation(command);
      if (receipt.ok !== true || receipt.preparationId !== operation.id || receipt.receiptId !== operation.id || receipt.ref !== `draft:${command.draftNumber}`) throw new Error('The confirmation response is incomplete. Refresh status or retry this exact confirmation.');
      await onComplete();
    } catch (e) {
      if (e.status === 409 && definiteRejections.has(e.code)) {
        setPending(null); setReview(null); setChecked(false);
        setError(`${e.message} This confirmation made no change. Refresh status or review the existing draft again.`);
      } else setError(`${e.message} Refresh status to see any completed adoption, or retry this exact confirmation. No invoice will be sent.`);
    } finally { setBusy(false); }
  };
  return <section aria-label={`Legacy invoice ${operation.id}`}>
    <p>Customer {operation.customerId} · legacy operation {operation.id}. The provider sets product prices. Review the existing draft; this action does not send an invoice.</p>
    <label>Existing legacy draft number <input type="number" min="1" value={draft} disabled={busy || !!pending} onChange={e => { setDraft(e.target.value); setReview(null); setChecked(false); }} /></label>
    <button type="button" onClick={preview} disabled={busy || !!pending || !Number.isSafeInteger(Number(draft)) || Number(draft) < 1}>Review existing legacy draft</button>
    {review && <div>
      <p>Provider prices in {review.observation.currency}. Net {review.observation.netAmount}; VAT {review.observation.vatAmount}; total {review.observation.grossAmount}.</p>
      {review.observation.lines.map(line => <p key={line.lineNumber}>{line.description}: {line.quantity} × {line.unitNetPrice}; discount {line.discountPercentage}%; net {line.totalNetAmount}.</p>)}
      <label>Reconciliation reason <textarea value={reason} minLength={10} maxLength={1000} disabled={busy || !!pending} onChange={e => setReason(e.target.value)} /></label>
      <label><input type="checkbox" checked={checked} disabled={busy || !!pending} onChange={e => setChecked(e.target.checked)} /> I reviewed this existing draft, its provider prices and total.</label>
      <button type="button" onClick={confirm} disabled={busy || (!pending && (!checked || reason.trim().length < 10))}>{pending ? 'Retry exact confirmation' : 'Adopt reviewed existing draft'}</button>
    </div>}
    {error && <p role="alert">{error}</p>}
    <button type="button" disabled={busy} onClick={onComplete}>Refresh invoice status</button>
  </section>;
}
